/*
 * Copyright (c) 2021. FINN Sp. z o.o.
 */

package pl.finn.forms.model

import io.kvision.core.*
import io.kvision.form.FormControl
import io.kvision.form.check.CheckBox
import io.kvision.form.check.RadioGroup
import io.kvision.form.select.Select
import io.kvision.form.select.SelectRemote
import io.kvision.form.select.selectOption
import io.kvision.form.text.Text
import io.kvision.form.text.TextArea
import io.kvision.form.time.DateTime
import io.kvision.html.*
import io.kvision.panel.FieldsetPanel
import io.kvision.panel.SimplePanel
import io.kvision.remote.RemoteOption
import io.kvision.types.toDateF
import io.kvision.utils.SnOn
import kotlinx.coroutines.*
import pl.finn.forms.POLE_WYMAGANE
import pl.finn.forms.enums.NodeType
import pl.finn.forms.generator.DocumentFormModel
import pl.finn.forms.generator.ElementMatcher
import pl.finn.forms.service.FormsServiceManager
import pl.finn.forms.service.IFormsService
import pl.finn.forms.utils.ControlType
import pl.finn.forms.utils.FormReadingUtils
import pl.finn.forms.utils.FormReadingUtils.checkBankAccount
import pl.finn.forms.utils.FormReadingUtils.checkMask
import pl.finn.forms.utils.form.FormBuilder
import pl.finn.forms.utils.form.FormElement
import kotlin.reflect.KSuspendFunction4


class Components(elementMatcher: ElementMatcher, reference: FormBuilder<DocumentTreeNode>) {

  private val formReference = reference
  private val matcher = elementMatcher

  private var previousElement = DocumentTreeNode()
  private var buttonHiddenDiv = Div()
  private var elementNo = 0
  private var controlType = ControlType()
  private var shouldBeFieldset = false

  suspend fun createChoiceElement(
    element: DocumentTreeNode,
    el: RadioGroup?,
    parentElements: DocumentTreeNode?
  ): Component {
    val maphiddenElement = HashMap<String, LinkedHashMap<DocumentTreeNode?, SimplePanel?>?>()
    element.value = getElementValue(element)
    if (element.value.isNullOrEmpty() && el != null) el.hide() else el?.show()
    val radioGroup = el ?: createRadioGroup(element, 0)
    val formElement = FormElement(
      element,
      radioGroup.apply {
        onEvent {
          change = {
            showHideNestedComponents(maphiddenElement)
          }
        }
      },
      required = element.metaModel?.isElementRequired == true,
      requiredMessage = POLE_WYMAGANE
    )
    formReference.getElement(formElement)

    return if (FormReadingUtils.isNextSiblingsELement(element) && FormReadingUtils.isNextSiblingChoice(element)) {
      val nextSiblingsGroup = parseNextSiblings(parentElements, element, maphiddenElement)
      val childrenGroup = createChildrenFildset(parentElements, element)
      changeRadioVisibility(maphiddenElement, (formElement.field as RadioGroup).value)
      Div {
        add(childrenGroup)
        add(formElement.field)
        add(nextSiblingsGroup)
      }
    } else if (shouldBeFieldset) {
      val nestedGroup = createNestedElementComponent(element, maphiddenElement)
      changeRadioVisibility(maphiddenElement, (formElement.field as RadioGroup).value)
      FieldsetPanel(FormReadingUtils.getLabel(element)) {
        (formElement.field as RadioGroup).label = ""
        add(formElement.field)
        add(nestedGroup)
        if (parentElements?.children != null && element.metaModel?.particleView?.get(0)?.addButtonName != null) {
          shouldRenderButtonDiv(element)
          add(createButtonGroup(element, parentElements.children))
        }
      }
    } else {
      val nestedGroup = createNestedElementComponent(element, maphiddenElement)
      changeRadioVisibility(maphiddenElement, (formElement.field as RadioGroup).value)
      Div {
        add(formElement.field)
        add(nestedGroup)
        if (parentElements?.children != null && element.metaModel?.particleView?.get(0)?.addButtonName != null) {
          shouldRenderButtonDiv(element)
          add(createButtonGroup(element, parentElements.children))
        }
      }
    }
  }

  suspend fun createSimpleChoiceElement(element: DocumentTreeNode, parentElements: DocumentTreeNode?): Component {
    val mapHiddenElement = HashMap<String, LinkedHashMap<DocumentTreeNode?, SimplePanel?>?>()
    val complexDiv = Div(className = "ml-md-2 pt-3")
    val selected =
      if (element.metaModel?.choiceView?.get(0)?.selected != null) element.metaModel?.choiceView?.get(0)?.selected.toString() else element.value
    for ((i, el) in element.metaModel?.choiceView?.get(0)?.labels?.withIndex()!!) {
      for (child in parentElements?.children!!) {
        if (child?.metaModel?.label.equals(el)) {
          val componentsEl = LinkedHashMap<DocumentTreeNode?, SimplePanel?>()
          val model = child?.let { matcher.matchElement(it, element) }
          matcher.usedElementsList.add(child)
          if (model != null) {
            complexDiv.add(model)
          }
          componentsEl[child] = model as SimplePanel?
          mapHiddenElement[i.toString()] = componentsEl
        }
      }
    }
    val optionList = ArrayList<StringPair>()
    var annotationLength = 0

    for ((i, option) in element.metaModel?.choiceView?.get(0)?.labels?.withIndex()!!) {
      option?.let { optionList.add(i.toString() to it) }
      annotationLength += option?.length!!
    }
    val radioGroup =
      RadioGroup(options = optionList, value = selected, inline = annotationLength < 80, rich = true).apply {
        this.id = element.id
        onEvent {
          change = {
            showHideNestedComponents(mapHiddenElement)
            element.metaModel?.choiceView?.get(0)?.selected = self.value?.toIntOrNull()
          }
        }
      }
    changeRadioVisibility(mapHiddenElement, selected)
    return Div() {
      add(radioGroup)
      add(complexDiv)
    }
  }

  private fun SnOn<RadioGroup>.showHideNestedComponents(maphiddenElement: HashMap<String, LinkedHashMap<DocumentTreeNode?, SimplePanel?>?>) {
    maphiddenElement.forEach { (key, value) ->
      value?.forEach {
        val control = formReference.getControlField(it.key)
        if (self.value == key) {
          control?.visible = true
          changeTreeVisibility(it.key, true)
          it.value?.show()
        } else {
          if (it.key?.metaModel?.choiceView != null) {
            it.key!!.value = null
          }
          control?.visible = false
          changeTreeVisibility(it.key, false)
          it.value?.hide()
        }
      }
    }
  }

  private fun changeTreeVisibility(element: DocumentTreeNode?, isVisible: Boolean) {
    if (element?.children != null) {
      var el = 0
      while (el < element.children.size) {
        val childControl = formReference.getControlField(element.children[el])
        childControl?.visible = isVisible
        if (FormReadingUtils.isSimpleChoiceElement(element.children[el]!!) ||
          (FormReadingUtils.isNextSiblingsELement(element.children[el]!!) && !element.children[el]?.metaModel?.choiceView.isNullOrEmpty() && FormReadingUtils.isNextSiblingChoice(element.children[el]!!))) {
          changeTreeVisibility(element.children[el], isVisible)
          el += element.children[el]?.metaModel?.choiceView?.get(0)?.labels?.size!!
        } else if (childControl !is RadioGroup) {
          changeTreeVisibility(element.children[el], isVisible)
        }
        el++
      }
    }
  }

  private fun createRadioGroup(element: DocumentTreeNode, choiceNo: Int): RadioGroup {
    val label = FormReadingUtils.getLabel(element)
    element.value = getElementValue(element)
    val optionList = ArrayList<StringPair>()
    var annotationLength = 0

    for ((i, option) in element.metaModel?.choiceView?.get(choiceNo)?.labels?.withIndex()!!) {
      option?.let { optionList.add(i.toString() to it) }
      annotationLength += option?.length!!
    }

    return RadioGroup(options = optionList, label = label, inline = annotationLength < 50, rich = true)
  }

  private fun getElementValue(element: DocumentTreeNode) =
    if (element.metaModel?.choiceView?.get(0)?.selected != null) element.metaModel?.choiceView?.get(0)?.selected.toString()
    else FormReadingUtils.addMetaValue(element)

  private suspend fun createNestedElementComponent(
    element: DocumentTreeNode, maphiddenElement: HashMap<String, LinkedHashMap<DocumentTreeNode?, SimplePanel?>?>
  ): Div {
    var lastIndex = 0
    val complexDiv = Div(className = "ml-md-2 pt-3")
    if (element.metaModel?.choiceView?.size!! > 1) {
      return createNestedChoice(element, maphiddenElement)
    }
    val selected =
      if (element.metaModel?.choiceView?.get(0)?.selected != null) element.metaModel?.choiceView?.get(0)?.selected.toString() else element.value
    for ((i, option) in element.children.withIndex()) {
      var componentsEl = LinkedHashMap<DocumentTreeNode?, SimplePanel?>()
      if (element.metaModel?.choiceView?.get(0)?.labels?.get(0)
          ?.matches("${option?.name}|${FormReadingUtils.getLabel(option!!)}") == true &&
        !element.metaModel?.choiceView?.get(0)?.labels?.get(0)?.equals(FormReadingUtils.getLabel(option!!))!!
      ) {
        for ((j, el) in element.metaModel?.choiceView?.get(0)?.labels?.withIndex()!!) {
          componentsEl = LinkedHashMap()
          var component: SimplePanel = Div()
          if (el?.contains(",") == true && el.length < 30) {
            el.split(",").toList().forEach { _ ->
              component = element.children[lastIndex]?.let { matcher.matchElement(it, element) }!! as SimplePanel
              componentsEl[element.children[lastIndex]] = component
              complexDiv.add(component)
              lastIndex++
            }
          } else {
            component = element.children[lastIndex]?.let { matcher.matchElement(it, element) }!! as SimplePanel
            componentsEl[element.children[lastIndex]] = component
            complexDiv.add(component)
            lastIndex++
          }
          maphiddenElement[j.toString()] = componentsEl
        }
        if (lastIndex <= element.children.size) {
          for (k in lastIndex until element.children.size) {
            val component = element.children[k]?.let { matcher.matchElement(it, element) } as SimplePanel
            complexDiv.add(component)
            shouldBeFieldset = true
          }
        }
        changeRadioVisibility(maphiddenElement, selected)
        return complexDiv
      } else if (element.metaModel?.choiceView?.get(0)?.labels?.size!! < element.children.size &&
        FormReadingUtils.getLabel(option!!)?.let { element.metaModel?.choiceView?.get(0)?.labels!!.contains(it) } == false
      ) {
        val component = option?.let { matcher.matchElement(it, element) } as? SimplePanel
        componentsEl[option] = component
        if (component != null) {
          complexDiv.add(component)
        }
        lastIndex++
        continue
      }
      val component = option?.let { matcher.matchElement(it, element) } as? SimplePanel
      componentsEl[option] = component
      if (component != null) {
        complexDiv.add(component)
      }
      maphiddenElement[i.toString()] = componentsEl
    }
    changeRadioVisibility(maphiddenElement, selected)
    return complexDiv
  }

  private suspend fun parseNextSiblings(
    parentElements: DocumentTreeNode?,
    element: DocumentTreeNode,
    maphiddenElement: HashMap<String, LinkedHashMap<DocumentTreeNode?, SimplePanel?>?>,
  ): Div {
    val complexDiv = Div(className = "ml-md-2 pt-3")
    var nodePosition = parentElements?.children?.indexOf(element) ?: 0
    while (nodePosition < (parentElements?.children?.size?.minus(1) ?: nodePosition) &&
      parentElements?.children?.get(nodePosition)?.name == element.name
    ) {
      nodePosition++
    }
    if (nodePosition < (parentElements?.children?.size?.minus(1) ?: nodePosition)) {
      val selectDiv = Div(className = "pt-3")
      for (i in 0 until element.metaModel?.choiceView!![0]?.labels?.size!!) {
        val componentsEl = LinkedHashMap<DocumentTreeNode?, SimplePanel?>()
        val option = parentElements?.children?.get(nodePosition)
        val component = option?.let { matcher.matchElement(it, parentElements) } as SimplePanel
        componentsEl[option] = component
        selectDiv.add(component)
        maphiddenElement[i.toString()] = componentsEl
        nodePosition++
      }
      complexDiv.add(selectDiv)
    }
    return complexDiv
  }

  private suspend fun createChildrenFildset(parentElements: DocumentTreeNode?, element: DocumentTreeNode): Div {
    val complexDiv = Div()
    var nodePosition = parentElements?.children?.indexOf(element) ?: 0
    while (nodePosition < (parentElements?.children?.size?.minus(1) ?: nodePosition) &&
      parentElements?.children?.get(nodePosition)?.name == element.name
    ) {
      val fieldsetDiv = FieldsetPanel(element.name)
      val option = parentElements?.children?.get(nodePosition)
      option?.children?.forEach { it ->
        if (it != null) {
          matcher.matchElement(it, option)?.let { it1 -> fieldsetDiv.add(it1 as SimplePanel) }
        }
      }
      complexDiv.add(fieldsetDiv)
      if (parentElements?.children != null && option?.metaModel?.particleView?.get(0)?.addButtonName != null) {
        shouldRenderButtonDiv(option)
        complexDiv.add(createButtonGroup(option, parentElements.children))
      }
      nodePosition++
    }
    return complexDiv
  }

  suspend fun createNestedChoice(
    element: DocumentTreeNode,
    maphiddenElement: HashMap<String, LinkedHashMap<DocumentTreeNode?, SimplePanel?>?>
  ): Div {
    val complexDiv = Div(className = "ml-md-2 pt-3")
    var componentsEl = LinkedHashMap<DocumentTreeNode?, SimplePanel?>()
    var previousEl = 0
    val selected =
      if (element.metaModel?.choiceView?.get(0)?.selected != null) element.metaModel?.choiceView?.get(0)?.selected.toString() else element.value
    for ((i, option) in element.children.withIndex()) {
      for ((j, el) in element.metaModel?.choiceView?.get(0)?.labels?.withIndex()!!) {
        var component: SimplePanel = Div()
        var isSimilar = false
        val elementLabel = option?.metaModel?.label
        val words: MutableSet<String>? = elementLabel?.lowercase()?.split(" ")?.toTypedArray()?.toMutableSet()
        if (words != null) {
          for (word in words) {
            val stringCompareMaxLength = if (word.length > 6) 6 else word.length
            isSimilar = el?.lowercase()?.contains(word.substring(0, stringCompareMaxLength)) == true
            if (isSimilar) {
              component = option.let { matcher.matchElement(it, element) }!! as SimplePanel
              if (j != previousEl) {
                val choice = elaborateChoice(element, componentsEl)
                complexDiv.add(choice)
                maphiddenElement[previousEl.toString()] = linkedMapOf(null to choice)
                componentsEl = LinkedHashMap()
                complexDiv.add(component)
              }
              componentsEl[option] = component
              previousEl = j
              break
            }
          }
        }
      }
      maphiddenElement[previousEl.toString()] = componentsEl
    }
    changeRadioVisibility(maphiddenElement, selected)
    return complexDiv
  }

  private fun elaborateChoice(
    element: DocumentTreeNode,
    componentsEl: LinkedHashMap<DocumentTreeNode?, SimplePanel?>
  ): Div {
    val complexDiv = Div(className = "ml-md-2 pt-3")
    val components1 = LinkedHashMap<DocumentTreeNode?, SimplePanel?>()
    val components2 = LinkedHashMap<DocumentTreeNode?, SimplePanel?>()
    val mapHiddenElement = HashMap<String, LinkedHashMap<DocumentTreeNode?, SimplePanel?>?>()
    val selected = element.metaModel?.choiceView?.get(1)?.selected.toString()
    var elNo = 0
    componentsEl.forEach { it ->
      if (elNo < 2) {
        components1[it.key] = it.value
      } else components2[it.key] = it.value
      it.value?.let { it1 -> complexDiv.add(it1) }
      elNo++
    }
    mapHiddenElement["0"] = components1
    mapHiddenElement["1"] = components2

    val optionList = ArrayList<StringPair>()
    var annotationLength = 0

    for ((i, option) in element.metaModel?.choiceView?.get(1)?.labels?.withIndex()!!) {
      option?.let { optionList.add(i.toString() to it) }
      annotationLength += option?.length!!
    }

    val radioGroup =
      RadioGroup(options = optionList, value = selected, inline = annotationLength < 50, rich = true).apply {
        onEvent {
          change = {
            showHideNestedComponents(mapHiddenElement)
          }
        }
      }
    changeRadioVisibility(mapHiddenElement, selected)
    return Div() {
      add(radioGroup)
      add(complexDiv)
    }
  }

  private fun changeRadioVisibility(
    maphiddenElement: HashMap<String, LinkedHashMap<DocumentTreeNode?, SimplePanel?>?>, choosen: String?
  ) {
    maphiddenElement.forEach { (key, values) ->
      if (values != null) {
        for(panel in values) {
          val control = formReference.getControlField(panel.key)
          if (choosen == key) {
            control?.visible = true
            changeTreeVisibility(panel.key, true)
            panel.value?.show()
          } else {
            if (panel.key?.metaModel?.choiceView != null) {
              panel.key!!.value = null
            }
            control?.visible = false
            changeTreeVisibility(panel.key, false)
            panel.value?.hide()
          }
        }
      }
    }
  }

  private suspend fun matchElementFromArray(
    option: ArrayList<DocumentTreeNode?>,
    componentsEl: ArrayList<SimplePanel>,
    isRadioChoose: Boolean,
  ): Div {
    val elementsDiv = Div(className = "ml-md-2")
    for (hiddenElement in option) {
      val component = hiddenElement?.let { matcher.matchElement(it, null) }
      if (component != null) {
        (component as SimplePanel).let { componentsEl.add(it) }
        if (isRadioChoose) component.show() else component.hide()
        elementsDiv.add(component)
      }
    }
    return elementsDiv
  }

  fun createDateTime(element: DocumentTreeNode): FormControl? {
    val label = if (!element.metaModel?.label.isNullOrEmpty()) element.metaModel?.label!! else element.label
    if (label?.matches("Do|Od".toRegex()) == true) return null
    element.value = FormReadingUtils.addMetaValue(element)
    val date = element.value?.toDateF("YYYY-MM-DD")
    val formElement = FormElement(
      element,
      DateTime(label = label, format = "YYYY-MM-DD") {
        if (!element.metaModel?.help.isNullOrEmpty()) {
          enableTooltip(TooltipOptions(element.metaModel?.help, rich = true, placement = Placement.BOTTOM))
        }
      },
      required = element.metaModel?.isElementRequired == true,
      requiredMessage = POLE_WYMAGANE,
    )
    formReference.getElement(formElement)
    formElement.field.setValue(date)
    if (element.name?.matches("TerminPlatnosci") == true) {
      formElement.field.input.apply {
        this.setAttribute("onchange", "FormUtils.applyComparisonDate(this);")
      }
    }
    return formElement.field
  }

  fun createInput(element: DocumentTreeNode, parentElements: DocumentTreeNode?): FormControl? {
    val label = FormReadingUtils.getLabel(element)
    val requirements = FormReadingUtils.checkValidation(element)
    if (element.type?.equals(NodeType.ATTR) == true && element.metaModel?.inputSize!! < 1 && element.metaModel?.help == null) return null
    element.value = FormReadingUtils.addMetaValue(element)
    val header = if (!element.metaModel?.header.isNullOrEmpty()) "<br><i style=\"display:inline-flex;\">${element.metaModel?.header!!}</i>" else ""
    val formElement = FormElement(
      element,
      Text(label = "$label $header", rich = true) {
        maxlength = if (element.typeName?.equals("Tekst10Typ") == true) 10 else 65
        if (!element.metaModel?.help.isNullOrEmpty()) {
          enableTooltip(TooltipOptions(element.metaModel?.help, rich = true, placement = Placement.BOTTOM))
        }
        if (parentElements?.children != null && element.metaModel?.particleView?.get(0)?.addButtonName != null) {
          shouldRenderButtonDiv(element)
          add(createButtonGroup(element, parentElements.children))
        }
      },
      required = element.metaModel?.isElementRequired == true,
      requiredMessage = POLE_WYMAGANE,
      validatorMessage = { requirements[1] }) {
      if (element.name?.matches("Zrachunku|Narachunek") == true) checkBankAccount(it.getValueAsString())
      else if (requirements[0] != null) it.getValueAsString()?.let { it1 -> (requirements[0])?.toRegex()?.matches(it1) }
      else true
    }
    formReference.getElement(formElement)

    if (element.typeName?.matches("decimal") == true) {
      formElement.field.input.apply {
        this.setAttribute("onkeyup", "FormUtils.applyCurrencyMask(this)")
      }
    } else checkMask(element.name)?.forEach {
      formElement.field.input.setAttribute(it.first, it.second)
    }
    return formElement.field
  }

  fun checkIfItShouldBeCheckboxOrLabel(element: DocumentTreeNode): Component {
    val label = if (!element.metaModel?.label.isNullOrEmpty()) element.metaModel?.label else element.label
    val value =
      if (!element.value.isNullOrEmpty()) element.value else if (element.metaModel?.value == "1") element.metaModel?.value else null
    return if ((label.isNullOrEmpty() || value.isNullOrEmpty()) || !element.metaModel?.help.isNullOrEmpty()) createLabel(
      element
    )
    else createCheckBox(element)
  }

  fun createCheckBox(element: DocumentTreeNode): FormControl {
    var label = ""
    element.value = FormReadingUtils.addMetaValue(element)

    label = FormReadingUtils.getLabel(element)!!
    val formElement = FormElement(
      element,
      CheckBox(label = label, rich = true) {
        if (!element.metaModel?.help.isNullOrEmpty()) {
          enableTooltip(TooltipOptions(element.metaModel?.help, rich = true, placement = Placement.BOTTOM))
        }
      },
      required = element.metaModel?.isElementRequired == true,
      requiredMessage = POLE_WYMAGANE
    )
    formReference.getElement(formElement)
    if (element.value.isNullOrEmpty() || element.value.equals("false")) formElement.field.setValue(false)
    else formElement.field.setValue(true)
    return formElement.field
  }

  fun createTextArea(element: DocumentTreeNode, parentElements: DocumentTreeNode?): FormControl {
    val label = FormReadingUtils.getLabel(element)
    val header = if (!element.metaModel?.header.isNullOrEmpty()) "<br><i>${element.metaModel?.header!!}</i>" else ""
    element.value = FormReadingUtils.addMetaValue(element)
    val formElement = FormElement(
      element,
      TextArea(
        label = "$label $header",
        rows = if (element.typeName?.matches("str:Tekst10000Typ|InformacjeTyp") == true) 15
        else if (element.typeName?.matches("2000|5000") == true || element.metaModel?.inputSize?.equals(2000) == true || element.metaModel?.inputSize?.equals(
            5000
          ) == true
        ) 8
        else 3,
        rich = true
      ) {
        if (!element.metaModel?.help.isNullOrEmpty()) {
          enableTooltip(TooltipOptions(element.metaModel?.help, rich = true, placement = Placement.BOTTOM))
        }
        maxlength =
          if (element.typeName?.matches("str:Tekst10000Typ|InformacjeTyp") == true) 10000
          else if (element.typeName?.matches("1000|2000|5000") == true || element.metaModel?.inputSize?.equals(2000) == true || element.metaModel?.inputSize?.equals(
              5000
            ) == true
          ) 2000
          else 200
        element.metaModel?.footer?.let { createFooter(it) }?.let { add(it) }
        if (parentElements?.children != null && element.metaModel?.particleView?.get(0)?.addButtonName != null) {
          shouldRenderButtonDiv(element)
          add(createButtonGroup(element, parentElements.children))
        }
      },
      required = element.metaModel?.isElementRequired == true,
      requiredMessage = POLE_WYMAGANE
    )
    formReference.getElement(formElement)

    return formElement.field
  }

  suspend fun createSelect(element: DocumentTreeNode, parentElement: DocumentTreeNode?): Div {
    val formControl = Div {}

    var label = FormReadingUtils.getLabel(element)
    val mapHiddenElement = HashMap<String, ArrayList<SimplePanel>?>()
    var selectOptions = ArrayList<String>()
    val value = FormReadingUtils.addMetaValue(element)
    val itContainsFooter = element.metaModel?.footer != null && element.metaModel?.footer?.length!! > 100
    if (label?.contains("#") == true) {
      selectOptions = label.split("#").toList() as ArrayList<String>
      selectOptions.removeAt(0)
      label = label.removeRange(label.indexOf("#"), label.length)
    }
    val formElement = if (!element.metaModel?.enumValues.isNullOrEmpty()) {
      FormElement(
        element,
        Select(label = label, value = element.value).apply {
          onEvent {
            change = {
              showHideNestedComponents(mapHiddenElement)
            }
          }
          emptyOption = true
          if (!element.metaModel?.help.isNullOrEmpty()) {
            enableTooltip(TooltipOptions(element.metaModel?.help, rich = true, placement = Placement.BOTTOM))
          }
          addCssClass("mr-0")
          if (!selectOptions.isEmpty()) {
            for ((i, enum) in selectOptions.withIndex()) {
              selectOption(i.toString(), enum)
            }
          } else {
            for ((i, enum) in element.metaModel?.enumValues!!.withIndex()) {
              if (itContainsFooter) {
                val footer = element.metaModel?.footer
                val index = enum?.let { footer?.indexOf(it) }
                selectOption(i.toString(), footer?.substring(index!!, footer.indexOf("</option>", index)))
              } else selectOption(i.toString(), enum)
            }
          }
          if (selectOptions.size > 10) {
            this.liveSearch = true
          }
          element.value = if (selectOptions.size == 1 || element.metaModel?.enumValues?.size!! == 1) "0" else value
          element.metaModel?.footer?.let { createFooter(it) }?.let { add(it) }
        },
        required = element.metaModel?.isElementRequired == true,
        requiredMessage = POLE_WYMAGANE
      )
    } else {
      FormElement(
        element,
        SelectRemote(
          FormsServiceManager,
          getProperFunction(element),
          value = DocumentFormModel.getToken(),
          label = label,
        ).apply {
          placeholder = "Szukaj"
          label = "Szukaj"
          setAttribute("title", "Szukaj")
          onEvent {
            change = {
              if (element.name.equals("KodPocztowy")) {
                formReference.getData()
                val scope = CoroutineScope(Job() + Dispatchers.Main)
                scope.launch {
                  val terytData = DocumentFormModel.getPostCodes(element)
                  for (el in parentElement?.children!!) {
                    if (el?.name.equals("Ulica")) {
                      (formReference.getControlField(el) as SelectRemote<*>).value = terytData[0].town
                    } else if (el?.name.equals("Poczta"))
                      (formReference.getControlField(el) as SelectRemote<*>).input.selectedLabel =
                        terytData[0].postOffice
                    else if (el?.name.equals("Miejscowosc"))
                      (formReference.getControlField(el) as SelectRemote<*>).input.selectedLabel = terytData[0].town
                  }
                }
              }
            }
          }
          if (!element.metaModel?.help.isNullOrEmpty()) {
            enableTooltip(TooltipOptions(element.metaModel?.help, rich = true, placement = Placement.BOTTOM))
          }
          addCssClass("mr-0")
          element.metaModel?.footer?.let { createFooter(it) }?.let { add(it) }
        },
        required = element.metaModel?.isElementRequired == true,
        requiredMessage = POLE_WYMAGANE
      )
    }
    formControl.add(formElement.field)

    if (element?.children != null) {
      val elementsList = element?.children!!.filter {
        it?.type != NodeType.ATTR
      }
      formControl.add(
        withContext(Dispatchers.Default) {
          createNestedSelectComponent(
            elementsList as ArrayList<DocumentTreeNode>,
            element,
            mapHiddenElement
          )
        }
      )
      formReference.getElement(formElement)
    }
    return formControl
  }

  private suspend fun getProperFunction(element: DocumentTreeNode): KSuspendFunction4<IFormsService, String?, String?, String?, List<RemoteOption>> {
    when (element.name) {
      "KodPocztowy" -> return IFormsService::getPostCodes
      "Poczta" -> return IFormsService::getPostOffice
      "Miejscowosc" -> return IFormsService::getTowns
      "Ulica" -> return IFormsService::getStreets
    }
    return IFormsService::getTowns
  }

  private fun SnOn<Select>.showHideNestedComponents(mapElement: HashMap<String, ArrayList<SimplePanel>?>) {
    mapElement.forEach { (key, value) ->
      val hiddenElement = value as ArrayList<SimplePanel>
      hiddenElement.forEach {
        if (self.value == key) {
          it.show()
        } else {
          if (it is Select) {
            it.value = null
          }
          it.hide()
        }
      }
    }
  }

  private suspend fun createNestedSelectComponent(
    elementsList: List<DocumentTreeNode>,
    element: DocumentTreeNode,
    mapElement: HashMap<String, ArrayList<SimplePanel>?>
  ): Div {
    val complexDiv = Div(className = "ml-md-2")
    elementsList.forEach {
      var optionhiddenElement = 0
      for ((i, el) in element.metaModel?.enumValues?.withIndex()!!) {
        if (el?.let { it1 -> it.label?.contains(it1) } == true) {
          optionhiddenElement = i
        }
      }
      val isSelectChoose = element.value == optionhiddenElement.toString()
      val componentsEl = ArrayList<SimplePanel>()
      complexDiv.add(matchElementFromArray(arrayListOf(it), componentsEl, isSelectChoose))
      mapElement[optionhiddenElement.toString()] = componentsEl
    }
    return complexDiv
  }

  private fun createLabel(element: DocumentTreeNode): Div {
    val label = FormReadingUtils.getLabel(element)
    val value = if (!element.metaModel?.help.isNullOrEmpty()) element.metaModel?.help else FormReadingUtils.addMetaValue(element)
    return Div(className = "mt-3 row") {
      div(label, className = "col-auto mb-0", rich = true)
      div(value, className = "col-auto text-bold", rich = true)
    }
  }

  fun createFooter(footerText: String): Component {
    return (
        P(
          footerText,
          rich = true,
          className = "pt-0 my-0"
        ) {
          fontSize = Pair(12, UNIT.px)
        }
        )
  }

  fun createButtonGroup(
    element: DocumentTreeNode,
    parentElements: ArrayList<DocumentTreeNode?>
  ): Div {
    var position = parentElements.indexOf(element) + 1
    return (
        Div(className = "p-2 d-flow-root") {
          buttonHiddenDiv = this

          if (elementNo > 1) {
            button(
              "Usuń",
              "far fa-minus-square",
              ButtonStyle.OUTLINEPRIMARY, className = "btn-sm"
            ).onClick {
              formReference.getData()
              parentElements.remove(element)
              DocumentFormModel.refreshForm()
            }
          }
          button(
            "Dodaj",
            "far fa-plus-square",
            ButtonStyle.OUTLINEPRIMARY, className = "btn-sm float-right"
          ).onClick {
            formReference.getData()
            val isNextSiblings = FormReadingUtils.isNextSiblingsELement(element) && FormReadingUtils.isNextSiblingChoice(element)
            parentElements.add(position, prepareElementToSerialization(element, isNextSiblings))
            DocumentFormModel.refreshForm()
          }
        }
        )
  }

  @OptIn(ExperimentalStdlibApi::class)
  private fun prepareElementToSerialization(element: DocumentTreeNode, isNextSiblings: Boolean): DocumentTreeNode {

    val copiedChoiceList = prepareChildrenToSerialisation(element)
    val elementMeta = MetaModel(
      element.metaModel?.id?.dropLast(8) + FormReadingUtils.getRandomString()
    ).apply {
      controlType = element.metaModel?.controlType
      choiceView = element.metaModel?.choiceView
      enumValues = element.metaModel?.enumValues
      footer = element.metaModel?.footer
      footerClass = element.metaModel?.footerClass
      header = element.metaModel?.header
      hidden = element.metaModel?.hidden
      headerClass = element.metaModel?.headerClass
      help = element.metaModel?.help
      html = element.metaModel?.html
      inputSize = element.metaModel?.inputSize
      label = element.metaModel?.label
      nodeName = element.metaModel?.nodeName
      nodeQName = element.metaModel?.nodeQName
      particleView = element.metaModel?.particleView
      validationMessage = element.metaModel?.validationMessage
      value = element.metaModel?.value ?: ""
      viewType = element.metaModel?.viewType
      isElementRequired = element.metaModel?.isElementRequired
      labelArray = element.metaModel?.labelArray
    }

    val node = DocumentTreeNode(
      element.id.dropLast(8) + FormReadingUtils.getRandomString(),
      element.name,
      element.type,
    ).apply {
      children = copiedChoiceList
      choiceLabels = element.choiceLabels
      label = element.label
      namespace = element.namespace
      parent = element.parent
      typeName = element.typeName
      typeNamespace = element.typeNamespace
      maxOccurs = element.maxOccurs
    }
    if (isNextSiblings) {
      elementMeta.choiceView = null
      node.metaModel = elementMeta
    } else {
      node.metaModel = elementMeta
    }
    return node
  }

  private fun prepareChildrenToSerialisation(element: DocumentTreeNode): ArrayList<DocumentTreeNode?> {
    val copiedChoiceList = ArrayList<DocumentTreeNode?>()
    element.children.forEach {
      it?.let { it1 -> prepareElementToSerialization(it1, false) }?.let { it2 -> copiedChoiceList.add(it2) }
    }
    return copiedChoiceList
  }

  @OptIn(ExperimentalStdlibApi::class)
  private fun getNextSiblings(
    element: DocumentTreeNode,
    parentElement: ArrayList<DocumentTreeNode?>
  ): ArrayList<DocumentTreeNode?> {
    val position = (parentElement.indexOf(element) ?: 0) + 1
    val elements = ArrayList<DocumentTreeNode?>()
    for (i in 0 until element.metaModel?.choiceView!![0]?.labels?.size!!) {
      elements.add(parentElement.get(position + i)?.let { prepareElementToSerialization(it, false) })
    }
    return elements
  }

  fun shouldRenderButtonDiv(element: DocumentTreeNode) {
    val label = FormReadingUtils.getLabel(element)
    val previousLabel = FormReadingUtils.getLabel(previousElement)
    if (previousLabel != null && previousLabel == label) {
      buttonHiddenDiv.hide()
      elementNo++
    } else if (elementNo > 0) {
      previousElement = element
      elementNo = 1
    } else {
      buttonHiddenDiv.hide()
      elementNo = 1
      previousElement = element
    }
  }
}
